import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Copy,
  Trash2,
  Save,
  Plus,
  Grid,
  Columns,
  QrCode
} from 'lucide-react';

const TOTAL_MINS = 1440; // 24 hours: 00:00 to 24:00

const timeStrToMins = (timeStr) => {
  if (!timeStr) return 450; // default 07:30
  const parts = String(timeStr).split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
};

const minsToTimeStr = (totalMins) => {
  const clamped = Math.max(0, Math.min(TOTAL_MINS, Math.round(totalMins)));
  if (clamped >= 1440) return '23:59:00';
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
};

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function DriverCardModal({
  driver,
  onClose,
  onSaveDriver,
  onReloadData,
  onOpenQr
}) {
  // Default and leftmost tab is 'info' as requested
  const [activeTab, setActiveTab] = useState('info'); // 'info' | 'shifts'
  const [driverData, setDriverData] = useState({ ...driver });
  const [driverShifts, setDriverShifts] = useState([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  // Agenda View mode: 'week' | 'month'
  const [viewMode, setViewMode] = useState('week');

  // Week selection state (starts at Monday of current week)
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()));

  // Month selection state (starts at 1st of current month)
  const [currentMonthDate, setCurrentMonthDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Quick add form state
  const [quickAddScope, setQuickAddScope] = useState('0'); // 0-6 for day index, or 'jours_ouvrables', 'tous_les_jours'
  const [quickAddStart, setQuickAddStart] = useState('07:30');
  const [quickAddEnd, setQuickAddEnd] = useState('17:00');
  const [quickAddSpecificDate, setQuickAddSpecificDate] = useState(formatDateISO(new Date()));

  const [isCopying, setIsCopying] = useState(false);

  // Drag resize state
  const [resizing, setResizing] = useState(null);

  // Compute 7 days of the selected week
  const weekDays = useMemo(() => {
    return DAY_NAMES.map((name, i) => {
      const d = new Date(currentMonday);
      d.setDate(d.getDate() + i);
      const iso = formatDateISO(d);
      const dayNum = String(d.getDate()).padStart(2, '0');
      const monthNum = String(d.getMonth() + 1).padStart(2, '0');
      return {
        index: i,
        name,
        date: d,
        iso,
        label: `${name} ${dayNum}/${monthNum}`
      };
    });
  }, [currentMonday]);

  // Compute Month Calendar Matrix
  const monthDays = useMemo(() => {
    const year = currentMonthDate.getFullYear();
    const month = currentMonthDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Monday of first week
    const startMon = getMonday(firstDayOfMonth);

    const days = [];
    const curr = new Date(startMon);

    // Generate weeks until after end of month
    while (curr <= lastDayOfMonth || curr.getDay() !== 1) {
      days.push({
        date: new Date(curr),
        iso: formatDateISO(curr),
        dayNum: curr.getDate(),
        isCurrentMonth: curr.getMonth() === month,
        isToday: formatDateISO(curr) === formatDateISO(new Date())
      });
      curr.setDate(curr.getDate() + 1);
    }

    return days;
  }, [currentMonthDate]);

  // Fetch driver shifts
  const fetchShifts = async () => {
    if (!driver?.id) return;
    setIsLoadingShifts(true);
    try {
      const res = await fetch(`/api/driver-shifts?driver_id=${driver.id}`);
      if (res.ok) {
        const data = await res.json();
        setDriverShifts(data);
      }
    } catch (err) {
      console.error('Error fetching driver shifts:', err);
    } finally {
      setIsLoadingShifts(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [driver?.id]);

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === 'week') {
      setCurrentMonday(prev => {
        const n = new Date(prev);
        n.setDate(n.getDate() - 7);
        return n;
      });
    } else {
      setCurrentMonthDate(prev => {
        const n = new Date(prev);
        n.setMonth(n.getMonth() - 1);
        return n;
      });
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      setCurrentMonday(prev => {
        const n = new Date(prev);
        n.setDate(n.getDate() + 7);
        return n;
      });
    } else {
      setCurrentMonthDate(prev => {
        const n = new Date(prev);
        n.setMonth(n.getMonth() + 1);
        return n;
      });
    }
  };

  const handleToday = () => {
    if (viewMode === 'week') {
      setCurrentMonday(getMonday(new Date()));
    } else {
      const d = new Date();
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      setCurrentMonthDate(d);
    }
  };

  // Quick Add Shift handler
  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddStart || !quickAddEnd) return;

    let targetDates = [];
    if (viewMode === 'week') {
      if (quickAddScope === 'jours_ouvrables') {
        targetDates = weekDays.slice(0, 5).map(d => d.iso);
      } else if (quickAddScope === 'tous_les_jours') {
        targetDates = weekDays.map(d => d.iso);
      } else {
        const idx = parseInt(quickAddScope, 10);
        if (weekDays[idx]) targetDates = [weekDays[idx].iso];
      }
    } else {
      targetDates = [quickAddSpecificDate];
    }

    for (const dateIso of targetDates) {
      const start_datetime = `${dateIso}T${quickAddStart}:00`;
      const end_datetime = `${dateIso}T${quickAddEnd}:00`;

      // Optimistic add
      const tempShift = {
        id: `temp_${Date.now()}_${Math.random()}`,
        driver_id: driver.id,
        start_datetime,
        end_datetime
      };
      setDriverShifts(prev => [...prev, tempShift]);

      try {
        await fetch('/api/driver-shifts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            driver_id: driver.id,
            start_datetime,
            end_datetime
          })
        });
      } catch (err) {
        console.error('Error adding shift:', err);
      }
    }

    await fetchShifts();
    if (onReloadData) onReloadData();
  };

  // Click on empty space in a column to create a shift (Week View)
  const handleColumnClick = async (dateIso, e) => {
    if (e.target.closest('.agenda-time-block')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const clickPercent = Math.max(0, Math.min(1, clickY / rect.height));
    const clickMins = clickPercent * TOTAL_MINS;

    const snappedStartMins = Math.floor(clickMins / 30) * 30;
    const snappedEndMins = Math.min(TOTAL_MINS, snappedStartMins + 480); // Default 8 hours

    const startStr = minsToTimeStr(snappedStartMins).slice(0, 5);
    const endStr = minsToTimeStr(snappedEndMins).slice(0, 5);

    const start_datetime = `${dateIso}T${startStr}:00`;
    const end_datetime = `${dateIso}T${endStr}:00`;

    const tempShift = {
      id: `temp_${Date.now()}`,
      driver_id: driver.id,
      start_datetime,
      end_datetime
    };
    setDriverShifts(prev => [...prev, tempShift]);

    try {
      await fetch('/api/driver-shifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driver_id: driver.id,
          start_datetime,
          end_datetime
        })
      });
      await fetchShifts();
      if (onReloadData) onReloadData();
    } catch (err) {
      console.error('Error creating clicked shift:', err);
    }
  };

  // Drag Resize handlers (Week View)
  const handleMouseDownResize = (e, shift, handle) => {
    e.stopPropagation();
    e.preventDefault();

    const columnElem = e.currentTarget.closest('.agenda-day-column');
    const colHeight = columnElem ? columnElem.getBoundingClientRect().height : 400;

    const shiftStart = String(shift.start_datetime);
    const shiftEnd = String(shift.end_datetime);
    const startTimePart = shiftStart.includes('T') ? shiftStart.split('T')[1].slice(0, 5) : shiftStart.split(' ')[1]?.slice(0, 5) || '07:30';
    const endTimePart = shiftEnd.includes('T') ? shiftEnd.split('T')[1].slice(0, 5) : shiftEnd.split(' ')[1]?.slice(0, 5) || '17:00';

    setResizing({
      shiftId: shift.id,
      handle,
      dateIso: shiftStart.slice(0, 10),
      initialY: e.clientY,
      initialStartMins: timeStrToMins(startTimePart),
      initialEndMins: timeStrToMins(endTimePart),
      colHeight
    });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const deltaY = e.clientY - resizing.initialY;
      const deltaMins = (deltaY / resizing.colHeight) * TOTAL_MINS;
      const snappedDeltaMins = Math.round(deltaMins / 15) * 15;

      setDriverShifts(prev => prev.map(s => {
        if (s.id !== resizing.shiftId) return s;

        const sStart = String(s.start_datetime);
        const sEnd = String(s.end_datetime);
        const datePart = sStart.slice(0, 10);

        if (resizing.handle === 'top') {
          const newStartMins = Math.max(0, Math.min(resizing.initialEndMins - 15, resizing.initialStartMins + snappedDeltaMins));
          const timeStr = minsToTimeStr(newStartMins);
          return {
            ...s,
            start_datetime: `${datePart}T${timeStr}`
          };
        } else {
          const newEndMins = Math.max(resizing.initialStartMins + 15, Math.min(TOTAL_MINS, resizing.initialEndMins + snappedDeltaMins));
          const timeStr = minsToTimeStr(newEndMins);
          return {
            ...s,
            end_datetime: `${datePart}T${timeStr}`
          };
        }
      }));
    };

    const handleMouseUp = async () => {
      const targetShift = driverShifts.find(s => s.id === resizing.shiftId);
      setResizing(null);

      if (targetShift && !String(targetShift.id).startsWith('temp_')) {
        try {
          await fetch(`/api/driver-shifts/${targetShift.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              start_datetime: targetShift.start_datetime,
              end_datetime: targetShift.end_datetime
            })
          });
          if (onReloadData) onReloadData();
        } catch (err) {
          console.error('Error updating resized shift:', err);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, driverShifts, onReloadData]);

  // Delete single shift
  const handleDeleteShift = async (e, shiftId) => {
    e.stopPropagation();
    setDriverShifts(prev => prev.filter(s => s.id !== shiftId));
    try {
      if (!String(shiftId).startsWith('temp_')) {
        await fetch(`/api/driver-shifts/${shiftId}`, { method: 'DELETE' });
        if (onReloadData) onReloadData();
      }
    } catch (err) {
      console.error('Error deleting shift:', err);
    }
  };

  // Copy Week Over Week (Takes currently selected week and copies to S+1)
  const handleCopyWeekSubmit = async () => {
    const sourceMonIso = weekDays[0].iso;
    const targetDate = new Date(currentMonday);
    targetDate.setDate(targetDate.getDate() + 7);
    const targetMonIso = formatDateISO(targetDate);

    setIsCopying(true);
    try {
      const res = await fetch('/api/driver-shifts/copy-week', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_monday_date: sourceMonIso,
          target_monday_date: targetMonIso,
          driver_id: driver.id
        })
      });
      if (res.ok) {
        const data = await res.json();
        await fetchShifts();
        if (onReloadData) onReloadData();
        // Automatically switch view to the target week
        setCurrentMonday(targetDate);
      }
    } catch (err) {
      console.error('Error copying week shifts:', err);
    } finally {
      setIsCopying(false);
    }
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    if (onSaveDriver) {
      await onSaveDriver(driver.id, driverData);
    }
    onClose();
  };

  // Header Title
  const endSunday = new Date(currentMonday);
  endSunday.setDate(endSunday.getDate() + 6);
  const weekLabel = `Semaine du ${currentMonday.getDate()} ${currentMonday.toLocaleDateString('fr-FR', { month: 'short' })} au ${endSunday.getDate()} ${endSunday.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
  const monthLabel = currentMonthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).toUpperCase();

  return (
    <div className="modal-overlay">
      <div
        className="overlay-card"
        style={{
          width: '960px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden'
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            background: '#1e293b',
            padding: '16px 20px',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#f8fafc', margin: 0 }}>
              {driver.first_name} {driver.last_name}
            </h2>
            <span
              style={{
                background: 'rgba(56, 189, 248, 0.15)',
                color: '#38bdf8',
                border: '1px solid #38bdf8',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.72rem',
                fontWeight: '700'
              }}
            >
              Permis {driver.driving_license || 'B'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {onOpenQr && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenQr(driver);
                }}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(56, 189, 248, 0.3)'
                }}
                title="Générer le QR code de connexion et gérer les appareils du chauffeur"
              >
                <QrCode size={14} />
                <span>Connexion QR</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* SUBTABS NAVIGATION (Informations leftmost & default) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: '#0f172a',
            borderBottom: '1px solid #334155'
          }}
        >
          <button
            type="button"
            className={`db-sub-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <User size={15} />
            <span>Informations</span>
          </button>

          <button
            type="button"
            className={`db-sub-tab ${activeTab === 'shifts' ? 'active' : ''}`}
            onClick={() => setActiveTab('shifts')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Clock size={15} />
            <span>Planning</span>
          </button>
        </div>

        {/* MODAL BODY */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* TAB 1: INFORMATIONS (Default) */}
          {activeTab === 'info' && (
            <form onSubmit={handleSaveInfo} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Prénom :</label>
                  <input
                    type="text"
                    className="search-input"
                    value={driverData.first_name || ''}
                    onChange={e => setDriverData({ ...driverData, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom :</label>
                  <input
                    type="text"
                    className="search-input"
                    value={driverData.last_name || ''}
                    onChange={e => setDriverData({ ...driverData, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Email :</label>
                  <input
                    type="email"
                    className="search-input"
                    value={driverData.email || ''}
                    onChange={e => setDriverData({ ...driverData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Téléphone :</label>
                  <input
                    type="text"
                    className="search-input"
                    value={driverData.phone || ''}
                    onChange={e => setDriverData({ ...driverData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#22c55e', display: 'block', marginBottom: '4px' }}>Permis de Conduire :</label>
                  <select
                    className="search-input"
                    value={driverData.driving_license || 'B'}
                    onChange={e => setDriverData({ ...driverData, driving_license: e.target.value })}
                  >
                    <option value="B">Permis B (Véhicules légers ≤ 3.5t)</option>
                    <option value="BE">Permis BE (Remorques)</option>
                    <option value="C1">Permis C1 (Camions 3.5t - 7.5t)</option>
                    <option value="C">Permis C (Poids lourds &gt; 7.5t)</option>
                    <option value="CE">Permis CE (Poids lourds articulés)</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#38bdf8', display: 'block', marginBottom: '4px' }}>Coût Horaire (CHF / h) :</label>
                  <input
                    type="number"
                    step="0.5"
                    className="search-input"
                    value={driverData.cost_per_hour || 35.00}
                    onChange={e => setDriverData({ ...driverData, cost_per_hour: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" className="btn-action" onClick={onClose}>Annuler</button>
                <button
                  type="submit"
                  className="btn-action"
                  style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '800', padding: '8px 18px' }}
                >
                  <Save size={15} />
                  <span>Enregistrer</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB 2: QUARTS DE TRAVAIL (AGENDA) */}
          {activeTab === 'shifts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* TOP CONTROLS: VIEW TOGGLE (WEEK / MONTH), NAVIGATION & COPY WEEK */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#1e293b',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid #334155',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}
              >
                {/* Navigation (Prev / Today / Next / Date Label) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="btn-action"
                    style={{ padding: '4px 8px', height: '30px' }}
                    title="Précédent"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={handleToday}
                    className="btn-action"
                    style={{ padding: '4px 10px', height: '30px', fontSize: '0.75rem', fontWeight: '700' }}
                  >
                    Aujourd'hui
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn-action"
                    style={{ padding: '4px 8px', height: '30px' }}
                    title="Suivant"
                  >
                    <ChevronRight size={16} />
                  </button>

                  <span style={{ fontSize: '0.86rem', fontWeight: '700', color: '#f8fafc', marginLeft: '6px' }}>
                    📅 {viewMode === 'week' ? weekLabel : monthLabel}
                  </span>
                </div>

                {/* Right Side: Week/Month View Toggle & Copy Week */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* View Mode Toggle: Semaine / Mois */}
                  <div
                    style={{
                      display: 'flex',
                      background: '#0f172a',
                      borderRadius: '6px',
                      padding: '2px',
                      border: '1px solid #334155'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setViewMode('week')}
                      style={{
                        background: viewMode === 'week' ? '#38bdf8' : 'transparent',
                        color: viewMode === 'week' ? '#0f172a' : '#94a3b8',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.74rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Columns size={12} />
                      <span>Semaine</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setViewMode('month')}
                      style={{
                        background: viewMode === 'month' ? '#38bdf8' : 'transparent',
                        color: viewMode === 'month' ? '#0f172a' : '#94a3b8',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.74rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Grid size={12} />
                      <span>Mois</span>
                    </button>
                  </div>

                  {/* Copy Week Button (Active in Week view) */}
                  {viewMode === 'week' && (
                    <button
                      type="button"
                      onClick={handleCopyWeekSubmit}
                      disabled={isCopying}
                      style={{
                        background: 'rgba(34, 197, 94, 0.15)',
                        color: '#22c55e',
                        border: '1px solid #22c55e',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        height: '30px'
                      }}
                      title="Copier tous les quarts de cette semaine vers la semaine suivante (S+1)"
                    >
                      <Copy size={13} />
                      <span>Copier S &rarr; S+1</span>
                    </button>
                  )}
                </div>
              </div>

              {/* 1. WEEK VIEW (24H VERTICAL AGENDA) */}
              {viewMode === 'week' && (
                <div
                  style={{
                    background: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}
                >
                  {/* Agenda Column Headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7, 1fr)', gap: '6px', textAlign: 'center' }}>
                    <div /> {/* Spacer for time ruler */}
                    {weekDays.map(day => {
                      const count = driverShifts.filter(s => String(s.start_datetime).startsWith(day.iso)).length;
                      return (
                        <div
                          key={day.iso}
                          style={{
                            background: '#1e293b',
                            padding: '6px 4px',
                            borderRadius: '4px',
                            border: '1px solid #334155',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                        >
                          <span style={{ fontSize: '0.76rem', fontWeight: '800', color: '#f8fafc' }}>
                            {day.name}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: '#38bdf8', fontWeight: '600' }}>
                            {day.iso.slice(8, 10)}/{day.iso.slice(5, 7)}
                          </span>
                          <span
                            style={{
                              fontSize: '0.62rem',
                              color: count > 0 ? '#22c55e' : '#64748b',
                              fontWeight: '700'
                            }}
                          >
                            {count} quart{count > 1 ? 's' : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Agenda Body: Left Ruler + 7 Columns */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '48px repeat(7, 1fr)',
                      gap: '6px',
                      position: 'relative',
                      height: '380px'
                    }}
                  >
                    {/* Left Time Ruler (00:00 to 24:00 every 2 hours) */}
                    <div
                      style={{
                        position: 'relative',
                        height: '100%',
                        userSelect: 'none'
                      }}
                    >
                      {Array.from({ length: 13 }).map((_, i) => {
                        const hour = i * 2;
                        const label = `${String(hour).padStart(2, '0')}:00`;
                        const topPercent = (i / 12) * 100;
                        return (
                          <span
                            key={i}
                            style={{
                              position: 'absolute',
                              top: `${topPercent}%`,
                              transform: 'translateY(-50%)',
                              right: '6px',
                              fontSize: '0.64rem',
                              color: '#64748b',
                              fontWeight: '700',
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>

                    {/* 7 Vertical Day Columns */}
                    {weekDays.map(day => {
                      const dayShifts = driverShifts.filter(s => String(s.start_datetime).startsWith(day.iso));

                      return (
                        <div
                          key={day.iso}
                          className="agenda-day-column"
                          onClick={(e) => handleColumnClick(day.iso, e)}
                          style={{
                            position: 'relative',
                            height: '380px',
                            background: 'rgba(30, 41, 59, 0.45)',
                            borderRadius: '4px',
                            border: '1px solid #1e293b',
                            cursor: 'crosshair',
                            overflow: 'hidden',
                            transition: 'background 0.15s ease'
                          }}
                          title={`Cliquer pour ajouter un quart le ${day.label}`}
                        >
                          {/* Hour guide lines every 2 hours */}
                          {Array.from({ length: 13 }).map((_, i) => (
                            <div
                              key={i}
                              style={{
                                position: 'absolute',
                                top: `${(i / 12) * 100}%`,
                                left: 0,
                                right: 0,
                                borderTop: '1px dashed rgba(51, 65, 85, 0.35)',
                                pointerEvents: 'none'
                              }}
                            />
                          ))}

                          {/* Render Shift Blocks */}
                          {dayShifts.map(shift => {
                            const sStart = String(shift.start_datetime);
                            const sEnd = String(shift.end_datetime);
                            const startPart = sStart.includes('T') ? sStart.split('T')[1].slice(0, 5) : sStart.split(' ')[1]?.slice(0, 5) || '07:30';
                            const endPart = sEnd.includes('T') ? sEnd.split('T')[1].slice(0, 5) : sEnd.split(' ')[1]?.slice(0, 5) || '17:00';

                            const sMins = timeStrToMins(startPart);
                            const eMins = timeStrToMins(endPart);
                            const topPct = Math.max(0, Math.min(100, (sMins / TOTAL_MINS) * 100));
                            const heightPct = Math.max(6, Math.min(100 - topPct, ((eMins - sMins) / TOTAL_MINS) * 100));

                            return (
                              <div
                                key={shift.id}
                                className="agenda-time-block"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: `${topPct}%`,
                                  height: `${heightPct}%`,
                                  left: '2px',
                                  right: '2px',
                                  background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.35) 0%, rgba(14, 165, 233, 0.55) 100%)',
                                  border: '1px solid #38bdf8',
                                  borderRadius: '4px',
                                  padding: '2px 4px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                  zIndex: 10,
                                  userSelect: 'none',
                                  boxSizing: 'border-box'
                                }}
                              >
                                {/* Top resize handle */}
                                <div
                                  onMouseDown={(e) => handleMouseDownResize(e, shift, 'top')}
                                  style={{
                                    height: '7px',
                                    cursor: 'ns-resize',
                                    margin: '-2px -4px 0 -4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Glisser pour modifier le début"
                                >
                                  <div style={{ width: '14px', height: '2px', background: '#7dd3fc', borderRadius: '1px' }} />
                                </div>

                                {/* Block Content */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px', minWidth: 0, overflow: 'hidden' }}>
                                  <span
                                    style={{
                                      fontSize: '0.64rem',
                                      fontWeight: '800',
                                      color: '#ffffff',
                                      lineHeight: '1.1',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}
                                  >
                                    {startPart} - {endPart}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteShift(e, shift.id)}
                                    style={{
                                      background: 'rgba(239, 68, 68, 0.85)',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '13px',
                                      height: '13px',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      padding: 0,
                                      flexShrink: 0
                                    }}
                                    title="Supprimer ce quart"
                                  >
                                    <X size={9} strokeWidth={3} />
                                  </button>
                                </div>

                                {/* Bottom resize handle */}
                                <div
                                  onMouseDown={(e) => handleMouseDownResize(e, shift, 'bottom')}
                                  style={{
                                    height: '7px',
                                    cursor: 'ns-resize',
                                    margin: '0 -4px -2px -4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Glisser pour modifier la fin"
                                >
                                  <div style={{ width: '14px', height: '2px', background: '#7dd3fc', borderRadius: '1px' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 2. MONTH VIEW (CALENDAR GRID) */}
              {viewMode === 'month' && (
                <div
                  style={{
                    background: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}
                >
                  {/* Month Day Headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', textAlign: 'center' }}>
                    {DAY_NAMES.map(name => (
                      <div
                        key={name}
                        style={{
                          background: '#1e293b',
                          padding: '6px 4px',
                          borderRadius: '4px',
                          border: '1px solid #334155',
                          fontSize: '0.75rem',
                          fontWeight: '800',
                          color: '#f8fafc'
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>

                  {/* Month Grid Cells */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '6px',
                      maxHeight: '380px',
                      overflowY: 'auto'
                    }}
                  >
                    {monthDays.map(day => {
                      const dayShifts = driverShifts.filter(s => String(s.start_datetime).startsWith(day.iso));

                      return (
                        <div
                          key={day.iso}
                          onClick={() => {
                            setQuickAddSpecificDate(day.iso);
                          }}
                          style={{
                            minHeight: '74px',
                            background: day.isCurrentMonth ? '#1e293b' : 'rgba(30, 41, 59, 0.3)',
                            opacity: day.isCurrentMonth ? 1 : 0.45,
                            border: day.isToday ? '1px solid #38bdf8' : '1px solid #334155',
                            borderRadius: '6px',
                            padding: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s ease'
                          }}
                        >
                          {/* Day Number Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: '800',
                                color: day.isToday ? '#38bdf8' : day.isCurrentMonth ? '#f8fafc' : '#64748b'
                              }}
                            >
                              {day.dayNum}
                            </span>
                            {dayShifts.length > 0 && (
                              <span
                                style={{
                                  background: 'rgba(56, 189, 248, 0.2)',
                                  color: '#38bdf8',
                                  fontSize: '0.62rem',
                                  fontWeight: '800',
                                  padding: '1px 5px',
                                  borderRadius: '3px'
                                }}
                              >
                                {dayShifts.length}
                              </span>
                            )}
                          </div>

                          {/* List of Shifts for this Day */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', overflowY: 'auto' }}>
                            {dayShifts.map(shift => {
                              const sStart = String(shift.start_datetime);
                              const sEnd = String(shift.end_datetime);
                              const startPart = sStart.includes('T') ? sStart.split('T')[1].slice(0, 5) : sStart.split(' ')[1]?.slice(0, 5) || '07:30';
                              const endPart = sEnd.includes('T') ? sEnd.split('T')[1].slice(0, 5) : sEnd.split(' ')[1]?.slice(0, 5) || '17:00';

                              return (
                                <div
                                  key={shift.id}
                                  style={{
                                    background: 'rgba(56, 189, 248, 0.2)',
                                    border: '1px solid rgba(56, 189, 248, 0.4)',
                                    borderRadius: '3px',
                                    padding: '2px 4px',
                                    fontSize: '0.64rem',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '2px'
                                  }}
                                >
                                  <span>{startPart}-{endPart}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteShift(e, shift.id)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                    title="Supprimer"
                                  >
                                    <X size={9} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 3. ADD CRÉNEAU ROW MOVED TO BOTTOM OF POPUP AS REQUESTED */}
              <form
                onSubmit={handleQuickAdd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#1e293b',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #334155',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#38bdf8', marginRight: '2px' }}>
                  <Clock size={16} />
                </div>

                {viewMode === 'week' ? (
                  <select
                    value={quickAddScope}
                    onChange={e => setQuickAddScope(e.target.value)}
                    style={{
                      background: '#0f172a',
                      color: '#f8fafc',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '4px 10px',
                      fontSize: '0.78rem',
                      height: '32px',
                      cursor: 'pointer'
                    }}
                  >
                    {weekDays.map(d => (
                      <option key={d.index} value={String(d.index)}>{d.label}</option>
                    ))}
                    <option value="jours_ouvrables">Jours ouvrables (Lun - Ven)</option>
                    <option value="tous_les_jours">Toute la semaine (Lun - Dim)</option>
                  </select>
                ) : (
                  <input
                    type="date"
                    value={quickAddSpecificDate}
                    onChange={e => setQuickAddSpecificDate(e.target.value)}
                    style={{
                      background: '#0f172a',
                      color: '#f8fafc',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '3px 8px',
                      fontSize: '0.78rem',
                      height: '32px'
                    }}
                    required
                  />
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="time"
                    value={quickAddStart}
                    onChange={e => setQuickAddStart(e.target.value)}
                    style={{
                      background: '#0f172a',
                      color: '#f8fafc',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '3px 6px',
                      fontSize: '0.78rem',
                      height: '32px'
                    }}
                    required
                  />
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>à</span>
                  <input
                    type="time"
                    value={quickAddEnd}
                    onChange={e => setQuickAddEnd(e.target.value)}
                    style={{
                      background: '#0f172a',
                      color: '#f8fafc',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '3px 6px',
                      fontSize: '0.78rem',
                      height: '32px'
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    background: '#22c55e',
                    color: '#0f172a',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0 14px',
                    height: '32px',
                    fontSize: '0.78rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Plus size={14} strokeWidth={2.5} />
                  <span>Ajouter</span>
                </button>
              </form>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
